/**
 * VALERANE - Système de Wishlist (Liste de souhaits)
 * Stockage local client-side haute performance, sans application externe.
 */

(function () {
  const STORAGE_KEY = 'valerane_wishlist';

  class WishlistManager {
    constructor() {
      this.items = this._load();
    }

    _load() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        console.warn('[Wishlist] Impossible de lire localStorage:', e);
        return [];
      }
    }

    _save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.warn('[Wishlist] Impossible d\'écrire dans localStorage:', e);
      }
      this._notify();
    }

    _notify() {
      const count = this.items.length;
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: {
            items: this.items,
            count: count,
          },
        })
      );
    }

    getAll() {
      return [...this.items];
    }

    getCount() {
      return this.items.length;
    }

    has(handleOrId) {
      if (!handleOrId) return false;
      const key = String(handleOrId);
      return this.items.some(
        (item) => String(item.handle) === key || String(item.id) === key
      );
    }

    add(product) {
      if (!product || !product.handle) return;
      if (!this.has(product.handle)) {
        this.items.push({
          id: product.id || '',
          handle: product.handle,
          title: product.title || '',
          price: product.price || '',
          compare_at_price: product.compare_at_price || '',
          image: product.image || '',
          url: product.url || `/products/${product.handle}`,
          added_at: Date.now(),
        });
        this._save();
      }
    }

    remove(handleOrId) {
      if (!handleOrId) return;
      const key = String(handleOrId);
      this.items = this.items.filter(
        (item) => String(item.handle) !== key && String(item.id) !== key
      );
      this._save();
    }

    toggle(product) {
      if (!product || !product.handle) return false;
      if (this.has(product.handle)) {
        this.remove(product.handle);
        return false;
      } else {
        this.add(product);
        return true;
      }
    }

    clear() {
      this.items = [];
      this._save();
    }
  }

  window.ValeraneWishlist = new WishlistManager();

  // Web Component pour le bouton Wishlist (Cœur)
  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.btn = this.querySelector('button') || this;
          this.handle = this.getAttribute('data-product-handle');
          this.productData = {
            id: this.getAttribute('data-product-id') || '',
            handle: this.handle,
            title: this.getAttribute('data-product-title') || '',
            price: this.getAttribute('data-product-price') || '',
            compare_at_price: this.getAttribute('data-product-compare-price') || '',
            image: this.getAttribute('data-product-image') || '',
            url: this.getAttribute('data-product-url') || '',
          };

          this.updateState();

          this.handleClick = this.handleClick.bind(this);
          this.handleUpdate = this.handleUpdate.bind(this);

          this.addEventListener('click', this.handleClick);
          document.addEventListener('wishlist:updated', this.handleUpdate);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.handleClick);
          document.removeEventListener('wishlist:updated', this.handleUpdate);
        }

        handleClick(e) {
          e.preventDefault();
          e.stopPropagation();

          const isAdded = window.ValeraneWishlist.toggle(this.productData);
          this.updateState();

          // Micro animation
          this.classList.add('is-animating');
          setTimeout(() => this.classList.remove('is-animating'), 300);
        }

        handleUpdate() {
          this.updateState();
        }

        updateState() {
          const isSaved = window.ValeraneWishlist.has(this.handle);
          if (isSaved) {
            this.classList.add('is-active');
            this.setAttribute('aria-pressed', 'true');
            if (this.btn) this.btn.setAttribute('aria-label', 'Retirer des favoris');
          } else {
            this.classList.remove('is-active');
            this.setAttribute('aria-pressed', 'false');
            if (this.btn) this.btn.setAttribute('aria-label', 'Ajouter aux favoris');
          }
        }
      }
    );
  }

  // Web Component pour le compteur de wishlist (Badge dans l'en-tête)
  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        connectedCallback() {
          this.updateCount();
          this.handleUpdate = this.handleUpdate.bind(this);
          document.addEventListener('wishlist:updated', this.handleUpdate);
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:updated', this.handleUpdate);
        }

        handleUpdate(e) {
          const count = e.detail ? e.detail.count : window.ValeraneWishlist.getCount();
          this.updateCount(count);
        }

        updateCount(countOverride) {
          const count = typeof countOverride === 'number' ? countOverride : window.ValeraneWishlist.getCount();
          const textEl = this.querySelector('[data-wishlist-count-text]') || this;
          textEl.textContent = String(count);

          const parentIcon = this.closest('.header-actions__wishlist-icon');
          if (count > 0) {
            this.classList.remove('hidden');
            this.style.display = 'flex';
            if (parentIcon) parentIcon.classList.add('header-actions__wishlist-icon--has-items');
          } else {
            this.classList.add('hidden');
            this.style.display = 'none';
            if (parentIcon) parentIcon.classList.remove('header-actions__wishlist-icon--has-items');
          }
        }
      }
    );
  }

  // Notifier au chargement de la page pour synchroniser tous les compteurs existants
  document.addEventListener('DOMContentLoaded', () => {
    window.ValeraneWishlist._notify();
  });
})();
